import { createSelector } from '@reduxjs/toolkit'
import isEqual from 'lodash/isEqual'
import uniq from 'lodash/uniq'

import { PRO_MODAL_PRODUCTS, PRODUCT_NAME_MAP } from 'common/billing/constants'
import { AddOnStatus, AddOnSlug } from 'common/billing/entities/addOns/interfaces'
import {
  getActiveAddOns,
  getAddOnsData,
  getInboxData,
} from 'common/billing/entities/addOns/selectors'
import {
  applyDiscountToCart,
  extractCartSlugs,
  getOfferingByAbilities,
} from 'common/billing/entities/cart/helpers'
import {
  CartProductAddOn,
  CartProductPlan,
  CartProductsData,
} from 'common/billing/entities/cart/interfaces'
import { getAppliedDiscount } from 'common/billing/entities/discount/selectors'
import {
  getActiveProProducts,
  getIsUpgradeFromTrialToPaidEnabled,
  getProProducts,
} from 'common/billing/entities/pro/selectors'
import { getSubscriptionInfo } from 'common/billing/entities/subscription/selectors'
import { RequestUpgradePayload } from 'common/billing/interfaces/requestUpgradePayload'
import { UpgradeRequest } from 'common/billing/interfaces/upgradeRequest'
import { AbilityState } from 'common/core/constants/AbilityState'
import { ProductType } from 'common/core/interfaces/products'
import { getAbilities } from 'common/core/selectors/abilitiesSelectors'

/**
 * Local usage from upgradeRequest selectors to handle cycle deps.
 */
const getUpgradeRequest = (state: RootState): UpgradeRequest => state.billing.upgradeRequest

export const getAddOnCartData = createSelector([getAddOnsData], (addOns): CartProductAddOn[] =>
  addOns
    .filter((addOn) => addOn.status !== AddOnStatus.ACTIVE)
    .map<CartProductAddOn>((addOn) => ({
      type: 'addOn',
      name: addOn.name,
      price: addOn.price,
      fullPrice: addOn?.full_price,
      slug: addOn.slug,
      requiredPlan: ProductType.AUTOMATION,
      order: 5,
    })),
)

export const getCartProductsUndiscounted = createSelector(
  [getUpgradeRequest, getProProducts, getAddOnCartData, getInboxData],
  (upgradeRequest, proProducts, addOns, inboxData): CartProductsData => {
    if (!proProducts) {
      return { plans: [], addOns: [] }
    }

    const automation: CartProductPlan = {
      type: 'plan',
      slug: ProductType.AUTOMATION,
      name: PRODUCT_NAME_MAP[ProductType.AUTOMATION],
      price: proProducts.automation.price,
      dynamicPrice: true,
      order: 0,
    }
    const inbox: CartProductPlan = {
      type: 'plan',
      slug: ProductType.INBOX,
      name: PRODUCT_NAME_MAP[ProductType.INBOX],
      price: inboxData.price.amount,
      dynamicPrice: true,
      order: 10,
    }
    const { strictRequest } = upgradeRequest
    const isInboxAsPlan = inboxData.isAvailable && !inboxData.isAddOn

    // Inbox-only use case
    const requestedPlans =
      upgradeRequest.products.length || strictRequest ? upgradeRequest.products : PRO_MODAL_PRODUCTS
    if (isEqual(requestedPlans, [ProductType.INBOX]) && isInboxAsPlan) {
      return { plans: [inbox], addOns: [] }
    }

    // Add-ons only case. Only plans that are not active and required by add-ons
    if (upgradeRequest.addOns?.length) {
      const addOnSlugs = upgradeRequest.addOns
      const requestedAddOns = addOns.filter((addOn) => addOnSlugs.includes(addOn.slug))
      const planSlugs = new Set(requestedAddOns.map((addOn) => addOn.requiredPlan))
      const requestedPlans = [automation].filter(
        (plan) =>
          planSlugs.has(plan.slug) &&
          (!proProducts[plan.slug]?.active || upgradeRequest.requestPaid),
      )

      return {
        plans: requestedPlans,
        addOns: requestedAddOns,
      }
    }

    const plans = [automation, isInboxAsPlan && inbox].filter(
      (plan) =>
        plan &&
        (!proProducts[plan.slug]?.active || upgradeRequest.requestPaid) &&
        requestedPlans.includes(plan.slug),
    ) as CartProductPlan[]

    // Add-ons from `UpgradeRequest` or any add-on
    const requestedAddOns =
      upgradeRequest.addOns?.length || strictRequest
        ? upgradeRequest.addOns
        : Object.values(AddOnSlug)

    return {
      plans,
      addOns: addOns.filter((addOn) => requestedAddOns.includes(addOn.slug)),
    }
  },
)

export const getCartProducts = createSelector(
  [getCartProductsUndiscounted, getAppliedDiscount],
  (cart, discount) => {
    if (discount) {
      return applyDiscountToCart(cart, discount)
    }

    return cart
  },
)

export const getDiscountedCartTotal = createSelector(
  [
    getCartProducts,
    getAppliedDiscount,
    (_: RootState, products: string[]) => products,
    (_: RootState, __: string[], addOns: string[]) => addOns,
  ],
  (cart, discount, products, addOns): number => {
    const filteredPlans = cart.plans.filter((plan) => products.includes(plan.slug))
    const filteredAddOns = cart.addOns.filter((addOn) => addOns.includes(addOn.slug))

    const filteredCart: CartProductsData = {
      plans: filteredPlans,
      addOns: filteredAddOns,
    }
    const discountedCart = discount ? applyDiscountToCart(filteredCart, discount) : filteredCart

    return [...discountedCart.plans, ...discountedCart.addOns].reduce(
      (sum, product) => sum + (product.discount?.price ?? product.price),
      0,
    )
  },
)

const getIsCartFulfilledByAbilities = createSelector(
  getAbilities,
  (_: RootState, requestUpgradePayload: RequestUpgradePayload) => requestUpgradePayload,
  (abilities, request) => {
    // if any product or add-ons is requested, check the product or add-on
    if (request.products?.length || request.addOns?.length) {
      return false
    }

    if (!request.abilities?.length) {
      return false
    }

    return request.abilities.every((ability) => abilities[ability]?.state === AbilityState.ENABLED)
  },
)

export const getIsCartFulfilled = createSelector(
  getSubscriptionInfo,
  getActiveProProducts,
  getActiveAddOns,
  (_: RootState, requestUpgradePayload: RequestUpgradePayload) => requestUpgradePayload,
  getIsUpgradeFromTrialToPaidEnabled,
  getIsCartFulfilledByAbilities,
  (
    info,
    activeProducts,
    activeAddOns,
    requestUpgradePayload,
    isUpgradeFromTrialToPaidEnabled,
    abilitiesAreMet,
  ) => {
    // Upgrade from Trial to Paid
    if (requestUpgradePayload.requestPaid && info.isTrial) {
      return false
    }
    if (requestUpgradePayload.requestPaid && isUpgradeFromTrialToPaidEnabled) {
      return false
    }

    if (abilitiesAreMet) {
      return true
    }

    const offeringByAbilities = getOfferingByAbilities(requestUpgradePayload.abilities)
    const requestedProducts = requestUpgradePayload.products || offeringByAbilities.products
    const requestedAddOns = Array.from(
      uniq([...(requestUpgradePayload.addOns || []), ...offeringByAbilities.addOns]),
    )

    const hasRequestedProducts = Boolean(requestedProducts && requestedProducts.length > 0)
    const hasRequestedAddOns = Boolean(requestedAddOns && requestedAddOns.length > 0)

    if (!hasRequestedProducts && !hasRequestedAddOns) {
      return activeProducts.length > 0
    }

    const isProductsFulfilled = (requestedProducts || []).every((product) =>
      activeProducts.includes(product as ProductType.INBOX | ProductType.AUTOMATION),
    )

    const isAddOnsFulfilled = (requestedAddOns || []).every((addOn) => activeAddOns.includes(addOn))

    return isProductsFulfilled && isAddOnsFulfilled
  },
)

export const isAiOnlyCart = (cart: CartProductsData): boolean => {
  const cartSlugs = extractCartSlugs(cart)

  return isEqual(cartSlugs, {
    plans: [],
    addOns: [AddOnSlug.AI],
  })
}

export const isInboxOnlyCart = (cart: CartProductsData): boolean => {
  const cartSlugs = extractCartSlugs(cart)
  return isEqual(cartSlugs, {
    plans: [ProductType.INBOX],
    addOns: [],
  })
}

export const getIsAiOnlyCart = createSelector([getCartProducts], (cart): boolean =>
  Boolean(isAiOnlyCart(cart)),
)
